import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  MoreHorizontal,
  CheckCircle2,
  Circle,
  ArrowUp,
  ArrowRight,
  ArrowDown,
  Loader2
} from 'lucide-react'
import { useInteractions } from '@/components/interactions/hooks/useInteractions'
import { useDebouncedValue } from '@/hooks/useDebounce'
import type { InteractionListItem } from '@/components/interactions/types'

// Helper functions
const extractAssistantType = (name: string): string => {
  const lower = name.toLowerCase()
  if (lower.includes('legal')) return 'Legal'
  if (lower.includes('story')) return 'Story'
  if (lower.includes('knowledge')) return 'Knowledge'
  return 'General'
}

const getAssistantColor = (type: string): string => {
  const colors: Record<string, string> = {
    Legal: 'bg-blue-100 text-blue-800 border-blue-200',
    Story: 'bg-purple-100 text-purple-800 border-purple-200',
    Knowledge: 'bg-green-100 text-green-800 border-green-200',
    General: 'bg-muted text-muted-foreground border-border',
  }
  return colors[type] || colors.General
}

const getPriority = (messageCount: number): { label: string; icon: typeof ArrowUp; color: string } => {
  if (messageCount > 10) {
    return { label: 'High', icon: ArrowUp, color: 'text-red-600' }
  }
  if (messageCount >= 5) {
    return { label: 'Medium', icon: ArrowRight, color: 'text-yellow-600' }
  }
  return { label: 'Low', icon: ArrowDown, color: 'text-muted-foreground' }
}

export function InteractionsList() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)

  const { data, isLoading, error } = useInteractions({
    page,
    pageSize,
    search: debouncedSearch,
    orderColumn: 2, // Last activity column
    orderDirection: 'desc',
  })

  const totalPages = data ? Math.ceil(data.recordsFiltered / pageSize) : 0

  const handleViewInteraction = (interaction: InteractionListItem) => {
    navigate(`/app/interactions/${interaction.ragflow_session_id}`)
  }


  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header Section */}
      <div className="border-b bg-background px-8 py-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold tracking-tight">Interactions</h1>
          <p className="text-muted-foreground mt-1">View and analyze chat interactions</p>
        </div>
      </div>

      {/* Filter & Action Bar */}
      <div className="border-b bg-background px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 flex-1">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter interactions..."
                className="pl-9"
              />
            </div>
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Status
            </Button>
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Assistant Type
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <Eye className="h-4 w-4 mr-2" />
              View
            </Button>
            <Button size="sm" className="bg-primary hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" />
              Add Interaction
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 px-8 py-6 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-16">
              <p className="text-destructive">Failed to load interactions</p>
              <p className="text-sm text-muted-foreground mt-1">Please try again</p>
            </div>
          )}

          {/* Table */}
          {!isLoading && !error && data && (
            <div className="border rounded-lg bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-32">ID</TableHead>
                    <TableHead className="w-32">Assistant</TableHead>
                    <TableHead>Visitor</TableHead>
                    <TableHead className="w-24 text-center">Messages</TableHead>
                    <TableHead className="w-24">Status</TableHead>
                    <TableHead className="w-24">Priority</TableHead>
                    <TableHead className="w-32">Last Activity</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-16 text-muted-foreground">
                        {debouncedSearch ? 'No matching interactions found' : 'No interactions yet'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.data.map((interaction) => {
                      const assistantType = extractAssistantType(interaction.assistant_name)
                      const priority = getPriority(interaction.message_count)
                      const PriorityIcon = priority.icon

                      return (
                        <TableRow
                          key={interaction.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleViewInteraction(interaction)}
                        >
                          {/* ID */}
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {interaction.ragflow_session_id.substring(0, 8)}
                          </TableCell>

                          {/* Assistant Tag */}
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={getAssistantColor(assistantType)}
                            >
                              {assistantType}
                            </Badge>
                          </TableCell>

                          {/* Visitor */}
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">{interaction.visitor_name}</span>
                              <span className="text-xs text-muted-foreground">
                                {interaction.visitor_email}
                              </span>
                            </div>
                          </TableCell>

                          {/* Messages */}
                          <TableCell className="text-center">
                            <Badge variant="secondary" className="font-mono">
                              {interaction.message_count}
                            </Badge>
                          </TableCell>

                          {/* Status */}
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              {interaction.is_read ? (
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              ) : (
                                <Circle className="h-4 w-4 text-muted-foreground" />
                              )}
                              <span className="text-sm">
                                {interaction.is_read ? 'Read' : 'Unread'}
                              </span>
                            </div>
                          </TableCell>

                          {/* Priority */}
                          <TableCell>
                            <div className={`flex items-center gap-1.5 ${priority.color}`}>
                              <PriorityIcon className="h-4 w-4" />
                              <span className="text-sm">{priority.label}</span>
                            </div>
                          </TableCell>

                          {/* Last Activity */}
                          <TableCell className="text-sm text-muted-foreground">
                            {interaction.last_activity}
                          </TableCell>

                          {/* Actions */}
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation()
                                  handleViewInteraction(interaction)
                                }}>
                                  View details
                                </DropdownMenuItem>
                                <DropdownMenuItem>Mark as {interaction.is_read ? 'unread' : 'read'}</DropdownMenuItem>
                                <DropdownMenuItem className="text-destructive">
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Pagination */}
      {!isLoading && !error && data && (
        <div className="border-t bg-background px-8 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {data.recordsFiltered} of {data.recordsTotal} row(s)
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page</span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(Number(value))
                    setPage(1)
                  }}
                >
                  <SelectTrigger className="h-8 w-16">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages || 1}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <span className="sr-only">Previous page</span>
                    ←
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages || totalPages === 0}
                  >
                    <span className="sr-only">Next page</span>
                    →
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
